import { Objects } from '../../shared/objects.js';
import { StateTracker } from '../state/state-tracker.js';
import { EventHelper } from './event-helper.js';
import { StateHelper } from './state-helper.js';
import { Unit } from './unit.js';

/**
 * Component
 *
 * This will allow components to be extended
 * from a single factory.
 *
 * @class
 * @augments Unit
 *
 * @param {...any} args
 *
 * @example
 * class QuickFlashPanel extends Component
 *	{
 *		render()
 *		{
 *			return {
 *
 *			};
 *		}
 *	}
 */
export class Component extends Unit
{
	/**
	 * This will create a component.
	 *
	 * @constructor
	 * @param {...any} args
	 */
	constructor(...args)
	{
		super(...args);

		/**
		 * @param {boolean} isComponent
		 */
		this.isComponent = true;

		/**
		 * @param {boolean} stateResumed
		 */
		this.stateResumed = false;

		/* this will allow the component to override the
		state target id to add a custom id */
		/**
		 * @type {string|null} stateTargetId // optional override of state id
		 */
		this.stateTargetId = null;
		this._setupData();
	}

	/**
	 * This will set the data.
	 *
	 * @returns {object|null}
	 */
	setData()
	{
		return null;
	}

	/**
	 * This will setup the component data.
	 *
	 * @protected
	 * @returns {void}
	 */
	_setupData()
	{
		if (this.data)
		{
			return;
		}

		const data = this.setData();
		if (data)
		{
			this.data = data;
		}
	}

	/**
	 *  This will resume the component scope when persisting.
	 *
	 * @param {{data: any, state: any, stateHelper: any, persistedChildren: any, id: string}} persistedLayout
	 * @returns {void}
	 */
	resumeScope(persistedLayout)
	{
		this.data = persistedLayout.data;
		this.state = persistedLayout.state;
		this.stateHelper = persistedLayout.stateHelper;
		this.persistedChildren = persistedLayout.persistedChildren;
		this.id = persistedLayout.id;
	}

	/**
	 * This will initialize the component.
	 *
	 * @protected
	 * @returns {void}
	 */
	initialize()
	{
		this.setupContext();
		this.addStates();
		this.beforeSetup();
	}

	/**
	 * This will activate the post build actions.
	 *
	 * @protected
	 * @returns {void}
	 */
	afterLayout()
	{
		this.addEvents();
		this.afterSetup();
	}

	/**
	 * This will setup the state target.
	 *
	 * @protected
	 * @param {string} [id]
	 * @returns {void}
	 */
	setupStateTarget(id)
	{
		const targetId = id || this.stateTargetId || this.id;
		this.state = StateTracker.getTarget(String(targetId));
	}

	/**
	 * Override this to setup the component states.
	 *
	 * @protected
	 * @returns {object}
	 *
	 * @example
	 * return {
	 *
	 * 	// simple state
	 * 	action: 'state', // any primitive value
	 *
	 * 	// a more complex state
	 * 	complexAction: {
	 * 		state: 'state', // any primitive value
	 * 		callBack(state, prevState)
	 * 		{
	 * 			// do something
	 * 		}
	 * 	}
	 * };
	 */
	setupStates()
	{
		/*
		return {
			action: 'state'
		};

		or

		return {
			action:
			{
				state: 'state',
				callBack(state, prevState)
				{

				}
			}
		};*/

		return {

		};
	}

	/**
	 * This will add the states.
	 *
	 * @protected
	 * @returns {void}
	 */
	addStates()
	{
		/* this will check to restore previous a previous state if the
		component has been preserved. */
		const state = this.state;
		if (state)
		{
			this.stateResumed = true;
			this.stateHelper.restore(state);
			return;
		}

		/* this will only setupa state manager if
		we have states */
		const states = this.setupStates();
		if (Objects.isEmpty(states))
		{
			return;
		}

		this.setStateHelper(states);
	}

	/**
	 * This will set the state helper.
	 *
	 * @param {object} [states]
	 * @returns {void}
	 */
	setStateHelper(states = {})
	{
		/**
		 * This will prevent the state helper from being
		 * reset.
		 */
		if (this.state)
		{
			return;
		}

		this.setupStateTarget();
		this.stateHelper = new StateHelper(this.state, states);
	}

	/**
	 * This will add a state.
	 *
	 * @param {object} state
	 * @returns {void}
	 */
	addState(state)
	{
		/**
		 * This will prevet new states from being added if the
		 * component has been resumed or the state helper is
		 * missing.
		 */
		if (!this.stateHelper || this.stateResumed == true)
		{
			return;
		}

		this.stateHelper.addStates(this.state, state);
	}

	/**
	 * This will remove the states.
	 *
	 * @protected
	 * @returns {void}
	 */
	removeStates()
	{
		const state = this.state;
		if (!state)
		{
			return;
		}

		this.stateHelper.removeRemoteStates(state);
		state.remove();
		this.stateResumed = false;
	}

	/**
	 * Sets a callback to be called when the component's data is flushed.
	 *
	 * @param {function} callBack
	 * @return {void}
	 */
	onFlush(callBack)
	{
		// @ts-ignore
		const data = this.data ?? this?.context?.data ?? null;
		if (data)
		{
			data.onFlush(callBack);
		}
	}

	/**
	 * This will set the event helper.
	 *
	 * @returns {void}
	 */
	setEventHelper()
	{
		/**
		 * This will prevent the event helper from being
		 * reset.
		 */
		if (this.events)
		{
			return;
		}

		this.events = new EventHelper();
	}

	/**
	 * This will setup the events.
	 *
	 * @protected
	 * @returns {Array<Array<any>>}
	 *
	 * @example
	 * return [
	 * 	// event, element, function, capture
	 * 	['click', this.element, function(e){}, false]
	 * ];
	 */
	setupEvents()
	{
		return [
			//['action', element, function(e){}, false]
		];
	}

	/**
	 * This will add the events.
	 *
	 * @protected
	 * @returns {void}
	 */
	addEvents()
	{
		const events = this.setupEvents();
		if (events.length < 1)
		{
			return;
		}

		this.setEventHelper();
		if (this.events)
		{
			this.events.addEvents(events);
		}
	}

	/**
	 * This will remove the events.
	 *
	 * @protected
	 * @returns {void}
	 */
	removeEvents()
	{
		const events = this.events;
		if (events)
		{
			events.reset();
		}
	}

	/**
	 * This will prepare the component to be destroyed.
	 *
	 * @protected
	 * @returns {void}
	 */
	prepareDestroy()
	{
		this.persistedCount = 0;
		this.rendered = false;
		this.beforeDestroy();
		this.removeEvents();
		this.removeStates();
		this.removeContext();

		/**
		 * Unlink data when not persisting to prevent memory leaks.
		 * Data watchers keep references to the component and DOM.
		 */
		if (this.data && this.persist !== true)
		{
			this.data.unlink();
		}
	}
}